import React, { useState, useEffect } from 'react';

const VoiceSelectionBuddy = ({ isVisible, onVoiceSelected }) => {
  const [currentVoiceIndex, setCurrentVoiceIndex] = useState(0);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSwipeAnimating, setIsSwipeAnimating] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState('');
  const [currentAudio, setCurrentAudio] = useState(null);

  const voiceOptions = [
    {
      id: 'emma',
      name: 'Emma',
      personality: 'Warm & Empathetic', 
      description: 'A caring, nurturing voice perfect for emotional support and personal reflection.',
      configId: '1c825d5e-2276-4205-9f02-b4f21fe65ec3',
      color: '#EC4899',
      previewUrl: '/sound/voice1.wav'
    },
    {
      id: 'bob',
      name: 'Bob',
      personality: 'Funny & Supportive',
      description: 'A balanced, professional voice that\'s great for work conversations and goal setting.',
      configId: 'a4962211-e08f-4fd2-9ec2-95bc68cb4f2b',
      color: '#4a90e2',
      previewUrl: '/sound/bob-voice.wav'
    },
    {
      id: 'alex',
      name: 'Alex',
      personality: 'Gen Z & Casual',
      description: 'A balanced, professional voice that\'s great for work conversations and goal setting.',
      configId: '24956749-78fe-48a9-a803-0a32a96a7a18',
      color: '#4a90e2',
      previewUrl: '/sound/alex-voice.wav'
    },
    {
      id: 'sage',
      name: 'Sage',
      personality: 'British & Thoughtful',
      description: 'A contemplative, insightful voice ideal for deep conversations and self-discovery.',
      configId: 'dab06340-fd3c-4461-96f7-30dc17bcbb14',
      color: '#059669',
      previewUrl: '/sound/voice4.wav'
    },
  ];

  const currentVoice = voiceOptions[currentVoiceIndex];
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      setIsSwipeAnimating(true);
      setSwipeDirection(isLeftSwipe ? 'left' : 'right');
      
      setTimeout(() => {
        if (isLeftSwipe) {
          nextVoice();
        }
        if (isRightSwipe) {
          prevVoice();
        }
        
        setTimeout(() => {
          setIsSwipeAnimating(false);
          setSwipeDirection('');
        }, 300);
      }, 100);
    }
  };

  const stopCurrentAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    setIsPreviewPlaying(false);
  };

  const playPreview = async () => {
    if (isPreviewPlaying) {
      stopCurrentAudio();
      return;
    }
    
    stopCurrentAudio();
    
    console.log('🎵 Playing preview:', currentVoice.previewUrl);
    setIsPreviewPlaying(true);
    
    try {
      let audioUrl = currentVoice.previewUrl;
      
      if (window.location.hostname !== 'localhost' && !audioUrl.startsWith('http')) {
        audioUrl = window.location.origin + audioUrl;
      }
      
      console.log('🌐 Final audio URL:', audioUrl);
      
      const audio = new Audio(audioUrl);
      audio.volume = 0.8;
      audio.preload = 'auto';
      audio.crossOrigin = 'anonymous';
      
      setCurrentAudio(audio);
      
      audio.addEventListener('ended', () => {
        console.log('🏁 Preview ended');
        setIsPreviewPlaying(false);
        setCurrentAudio(null);
      });
      
      audio.addEventListener('error', (e) => {
        console.error('❌ Audio error:', e.target.error);
        console.error('❌ Failed URL:', audioUrl);
        setIsPreviewPlaying(false);
        setCurrentAudio(null);
        alert(`Audio preview unavailable for ${currentVoice.name}. You can still select this voice.`);
      });
      
      audio.addEventListener('canplaythrough', () => {
        console.log('✅ Audio ready to play');
      });
      
      await audio.play();
      console.log('▶️ Preview playing successfully');
      
    } catch (error) {
      console.error('💥 Preview play failed:', error);
      setIsPreviewPlaying(false);
      setCurrentAudio(null);
      
      if (error.name === 'NotAllowedError') {
        console.error('🚫 Audio blocked by browser policy');
        alert('Audio previews are blocked by your browser. You can still select voices.');
      } else if (error.name === 'NotSupportedError') {
        console.error('🎵 Audio format not supported');
        alert(`Audio format not supported for ${currentVoice.name} preview.`);
      } else {
        console.error('🌐 Network or file error');
        alert(`Could not load audio preview for ${currentVoice.name}.`);
      }
    }
  };

  const handleBuddyClick = (e) => {
    e.stopPropagation();
    playPreview();
  };

  const nextVoice = () => {
    stopCurrentAudio();
    setIsSwipeAnimating(true);
    setSwipeDirection('left');
    
    setTimeout(() => {
      setCurrentVoiceIndex((prev) => (prev + 1) % voiceOptions.length);
      
      setTimeout(() => {
        setIsSwipeAnimating(false);
        setSwipeDirection('');
      }, 300);
    }, 100);
  };

  const prevVoice = () => {
    stopCurrentAudio();
    setIsSwipeAnimating(true);
    setSwipeDirection('right');
    
    setTimeout(() => {
      setCurrentVoiceIndex((prev) => (prev - 1 + voiceOptions.length) % voiceOptions.length);
      
      setTimeout(() => {
        setIsSwipeAnimating(false);
        setSwipeDirection('');
      }, 300);
    }, 100);
  };

  const selectVoice = () => {
    setSelectedVoice(currentVoice);
    stopCurrentAudio();
    console.log('Voice selected:', currentVoice.name);
    
    setTimeout(() => {
      onVoiceSelected(currentVoice);
    }, 500);
  };

  useEffect(() => {
    return () => {
      stopCurrentAudio();
    };
  }, []);

  useEffect(() => {
    if (!isVisible) {
      stopCurrentAudio();
    }
  }, [isVisible]);

  const getBuddyClasses = () => {
    let classes = 'buddy-circle';
    if (isPreviewPlaying) {
      classes += ' speaking';
    }
    if (isSwipeAnimating) {
      classes += ` swipe-${swipeDirection}`;
    }
    return classes;
  };

  if (!isVisible) return null;

  return (
    <div className="voice-selection-overlay">
      <div className="voice-selection-container">
        <div className="voice-header">
          <h2 className="voice-title">Pick a voice</h2>
        </div>

        <div 
          className="ai-alex-buddy-container"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="ai-buddy">
            <div 
              className={getBuddyClasses()} 
              style={{ '--voice-color': currentVoice.color }}
              onClick={handleBuddyClick}
            >
              <div className="buddy-inner">
                <div className="wave-animation"></div>
                <div className="buddy-core-alex"></div>
              </div>
            </div>
            
            <div className={`dots-alex-container ${isPreviewPlaying ? 'show' : ''}`}>
              <div className="dot alex dot-1"></div>
              <div className="dot alex dot-2"></div>
              <div className="dot alex dot-3"></div>
              <div className="dot alex dot-4"></div>
            </div>
          </div>

          <div className="voice-info">
            <h3 className="voice-name">{currentVoice.name}</h3>
            <p className="voice-personality">{currentVoice.personality}</p>
          </div>

          <div className="voice-controls">
            <button className="nav-button prev desktop-only" onClick={prevVoice}>←</button>
            <button 
              className={`preview-button ${isPreviewPlaying ? 'playing' : ''}`}
              onClick={playPreview}
            >
              {isPreviewPlaying ? 'Stop' : 'Play'}
            </button>
            <button className="nav-button next desktop-only" onClick={nextVoice}>→</button>
          </div>

          <div className="voice-indicators">
            {voiceOptions.map((_, index) => (
              <div
                key={index}
                className={`indicator ${index === currentVoiceIndex ? 'active' : ''}`}
                onClick={() => setCurrentVoiceIndex(index)}
              />
            ))}
          </div>

          <button className="select-voice-button" onClick={selectVoice}>
            Select
          </button>
        </div>
        
        <div className="swipe-instruction mobile-only">
          swipe to explore
        </div>
      </div>

      <style jsx>{`
        .voice-selection-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(170deg, #fefefed8 0%, #fefefec3 60%, #e0e7fcb2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 16px;
        }

        .voice-selection-container {
          width: 100%;
          max-width: 320px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .voice-header {
          text-align: center;
          position: fixed;
          top: 40px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 999;
        }

        .voice-title {
          color: #333;
          font-size: 16px;
          font-weight: 500;
          margin: 0;
          letter-spacing: -0.3px;
        }

        .ai-alex-buddy-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin-top: 20px;
        }

        .ai-buddy {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .buddy-circle {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: var(--voice-color, #4a90e2);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }

        .buddy-circle:active {
          transform: scale(0.95);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .buddy-inner {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .wave-animation {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 2px solid rgba(255, 255, 255, 0.3);
          animation: none;
        }

        .buddy-core-alex {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          position: relative;
          z-index: 2;
        }

        .buddy-circle.speaking .wave-animation {
          animation: wave 1s infinite ease-in-out;
        }

        @keyframes wave {
          0%, 100% {
            transform: scale(1);
            opacity: 0.4;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.2;
          }
        }

        .dots-alex-container {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 4px;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .dots-alex-container.show {
          opacity: 1;
        }

        .dot {
          width: 4px;
          height: 4px;
          background: rgba(0, 0, 0, 0.4);
          border-radius: 50%;
        }

        .dot.alex {
          animation: bounce 1.4s infinite ease-in-out;
        }

        .dot-1 { animation-delay: 0s; }
        .dot-2 { animation-delay: 0.2s; }
        .dot-3 { animation-delay: 0.4s; }
        .dot-4 { animation-delay: 0.6s; }

        @keyframes bounce {
          0%, 80%, 100% {
            transform: translateY(0);
            opacity: 0.5;
          }
          40% {
            transform: translateY(-8px);
            opacity: 1;
          }
        }

        .voice-info {
          text-align: center;
          margin: 8px 0 4px 0;
        }

        .voice-name {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          color: #333;
          letter-spacing: -0.2px;
        }

        .voice-personality {
          font-size: 12px;
          margin: 4px 0 0 0;
          opacity: 0.65;
          color: #666;
          font-weight: 400;
        }

        .voice-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          width: 100%;
          margin: 2px 0;
        }

        .nav-button {
          width: 28px;
          height: 28px;
          border: 1px solid rgba(0, 0, 0, 0.12);
          background: rgba(255, 255, 255, 0.95);
          color: #666;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .nav-button:active {
          background: rgba(255, 255, 255, 1);
          transform: scale(0.95);
        }

        .desktop-only {
          display: flex;
        }

        .mobile-only {
          display: none;
        }

        .preview-button {
          padding: 8px 18px;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(0, 0, 0, 0.12);
          border-radius: 20px;
          color: #333;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          letter-spacing: -0.1px;
        }

        .preview-button:active {
          background: rgba(255, 255, 255, 1);
          transform: scale(0.98);
        }

        .preview-button.playing {
          animation: pulse-button 1.2s infinite ease-in-out;
        }

        @keyframes pulse-button {
          0%, 100% {
            opacity: 0.8;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.03);
          }
        }

        .voice-indicators {
          display: flex;
          gap: 6px;
          margin: 6px 0 0 0;
        }

        .indicator {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.25);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .indicator.active {
          background: #333;
          width: 20px;
          border-radius: 2.5px;
        }

        .select-voice-button {
          width: 100%;
          max-width: 140px;
          padding: 10px 24px;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(0, 0, 0, 0.12);
          border-radius: 20px;
          color: #333;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          letter-spacing: -0.1px;
          margin-top: 4px;
        }

        .select-voice-button:active {
          background: rgba(255, 255, 255, 1);
          transform: scale(0.98);
        }

        .swipe-instruction {
          color: #666;
          font-size: 11px;
          text-align: center;
          opacity: 0.5;
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 999;
          letter-spacing: -0.2px;
        }

        .buddy-circle.swipe-left {
          animation: swipeLeftFeedback 0.4s ease-out;
        }

        .buddy-circle.swipe-right {
          animation: swipeRightFeedback 0.4s ease-out;
        }

        @keyframes swipeLeftFeedback {
          0% {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
          50% {
            transform: translateX(-15px) scale(0.95);
            opacity: 0.7;
          }
          100% {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes swipeRightFeedback {
          0% {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
          50% {
            transform: translateX(15px) scale(0.95);
            opacity: 0.7;
          }
          100% {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }

        @media (min-width: 481px) {
          .voice-selection-container {
            max-width: 400px;
            gap: 16px;
          }

          .buddy-circle {
            width: 120px;
            height: 120px;
          }

          .voice-title {
            font-size: 18px;
          }

          .voice-name {
            font-size: 20px;
          }

          .voice-personality {
            font-size: 13px;
          }

          .preview-button {
            padding: 10px 22px;
            font-size: 14px;
          }

          .select-voice-button {
            max-width: 160px;
            padding: 12px 28px;
            font-size: 15px;
          }

          .nav-button {
            display: flex;
          }
        }
      `}</style>
    </div>
  );
};

export default VoiceSelectionBuddy;